import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { OrderService } from '../../../services/order.service';
import { Order } from '../../../model/interfaces/order';
import { PlayerService, OffCanvasService } from 'src/app/services';
import { Fee } from 'src/app/model/interfaces/fee';
import { PaymentModel } from 'src/app/model/enums/payment-model';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CheckoutState } from 'src/app/model/enums/checkout-state';
import { InfoPricingUpdateComponent } from 'src/app/components/info-pricing-update.component/info-pricing-update.component';
import { PricingUpdateComponent } from '../pricing-update.component/pricing-update.component';
import { OrderDisplayDataService } from 'src/app/services/order-display-data.service';
import { OrderDisplayData, OrderLine } from 'src/app/model/interfaces/order-display-data';
import { PaymentInterval } from '../../../model/types/payment-interval';
import { TrackingFacadeService } from 'src/app/services/tracking/tracking-facade.service';

@Component({
  selector: 'app-basket-purchase',
  templateUrl: './basket-purchase.component.html',
  styleUrls: ['./basket-purchase.component.scss'],
})
export class BasketPurchaseComponent implements OnInit, OnDestroy {
  public order: Order;
  public topPrize: number;
  public minPrize: number;
  public winChance: number;
  public priceTotal: number;
  public packageOrderLines;
  public shareOrderLines;
  public itemCount: number;
  public fees: Fee[] = [];
  public paymentClass: string;
  public pricePrMonth: number;
  public playerLoggedIn = false;
  public pricingUpdateExpanded = false;
  public priceChangeInEffect = false;

  @Input() public checkoutState: CheckoutState;

  @Input('navigate-checkout')
  public navigateCheckout: boolean;

  @Input() showFees: boolean;

  @Input('show-per-month')
  public showPerMonth: boolean = false;

  @Input('payment-details')
  public paymentDetailsVisible: boolean;

  @Input('isBasketPage')
  public isBasketPage: boolean;

  @Input('isCheckout')
  public isCheckout: boolean;

  @Input('payment-model')
  public paymentModel: PaymentModel = PaymentModel.Subscription;

  @Input('price-factor')
  public classCount: PaymentInterval = 6;

  @Output() public onCloseClick = new EventEmitter();

  @ViewChild('pricingUpdate')
  public pricingUpdate: PricingUpdateComponent;

  // Represents the first day of the five draw dates in the 1st possible class for an internet internet sale.
  public drawDateLast: Date;

  // Represents the last date of the five draw dates in the last class for an internet sale.
  public drawDateFirst: Date;
  public orderLines: OrderLine[];
  public disabled: boolean;
  public isBsAppActive: boolean;

  private readonly orderService: OrderService;
  private readonly playerService: PlayerService;
  private readonly offCanvasService: OffCanvasService;
  private readonly orderDisplayDataService: OrderDisplayDataService;
  private readonly trackingFacadeService: TrackingFacadeService;

  private readonly router: Router;
  private onOrderDisplayDataUpdated = new Subscription();
  private onOrderExpiredSubscription = new Subscription();
  private onOrderExpirationNoticeSubscription = new Subscription();
  private playerSubscription = new Subscription();
  private priceSubscription = new Subscription();

  public constructor(
    orderService: OrderService,
    playerService: PlayerService,
    router: Router,
    offCanvasService: OffCanvasService,
    orderDisplayDataService: OrderDisplayDataService,
    trackingFacadeService: TrackingFacadeService
  ) {
    this.orderService = orderService;
    this.playerService = playerService;
    this.router = router;
    this.offCanvasService = offCanvasService;
    this.orderDisplayDataService = orderDisplayDataService;
    this.trackingFacadeService = trackingFacadeService;
  }

  public ngOnInit(): void {
    this.orderDisplayDataService.setProperties(
      this.paymentModel,
      this.isBsAppActive,
      this.checkoutState
    );
    // this.update();
    this.getOrderDisplayData();

    this.setupSubscriptions();
    this.setPaymentClass();
    this.getPlayerIsLoggedIn();
  }

  public close(): void {
    this.onCloseClick.emit();
  }

  private setPaymentClass(): void {
    this.paymentClass = `payment-model-${PaymentModel[this.paymentModel].toLowerCase()}`;
  }

  public stringToNumber(str: string): number {
    return Number(str);
  }

  private async getOrderDisplayData() {
    const displayData = await this.orderDisplayDataService.getOrderDisplayData();
    this.updateDisplayData(displayData);
  }

  private setupSubscriptions(): void {
    this.onOrderDisplayDataUpdated =
      this.orderDisplayDataService.onOrderDisplayDataUpdated.subscribe(
        (orderDisplayData: OrderDisplayData) => {
          this.updateDisplayData(orderDisplayData);
        }
      );

    this.onOrderExpiredSubscription = this.orderService.onOrderExpired.subscribe(() =>
      this.terminate()
    );
    this.onOrderExpirationNoticeSubscription = this.orderService.onOrderExpirationNotice.subscribe(
      () => this.warnTermination()
    );
  }

  private updateDisplayData(orderDisplayData: OrderDisplayData): void {
    try {
      this.topPrize = orderDisplayData.topPrize;
      this.minPrize = orderDisplayData.minPrize;
      this.winChance = orderDisplayData.winChance;
      this.priceTotal = orderDisplayData.priceTotal;
      this.order = orderDisplayData.order;
      this.orderLines = orderDisplayData.orderLines;
      this.packageOrderLines = orderDisplayData.packageOrderLines;
      this.shareOrderLines = orderDisplayData.shareOrderLines;
      this.itemCount = orderDisplayData.itemCount;
      this.fees = orderDisplayData.fees;
      this.paymentClass = orderDisplayData.paymentClass;
      this.pricePrMonth = orderDisplayData.pricePrMonth;
      this.priceChangeInEffect = orderDisplayData.priceChangeInEffect;
    } catch (error) {
      console.error(error);
    }
   
  }

  private terminate(): void {
    this.order = null;
    this.close();
  }

  private warnTermination(): void {
    console.log('terminates in a short bit');
  }

  public animateButtonMobile(): void {
    if (this.disabled) {
      return;
    }
    this.disabled = true;
  }

  /**
   * Handles the "Fortsæt til køb" CTA click: tracks the click then runs the button animation.
   * Navigation to /betaling is handled by the routerLink on the button.
   */
  public onContinueToPaymentClick(): void {
    this.trackingFacadeService.continueToPaymentClick();
    this.animateButtonMobile();
  }

  public getPlayerIsLoggedIn(): void {
    this.playerSubscription = this.playerService
      .getAuthorizedObservable()
      .subscribe((isAutherizered) => {
        if (isAutherizered === true) {
          this.playerLoggedIn = true;
        } else {
          this.playerLoggedIn = false;
        }
      });
  }

  public onPaymentClicked(): void {
    this.router.navigateByUrl('/min-side/mine-lodsedler?action=openshop');
  }

  public onPricingDetailsClick(): void {
    this.pricingUpdate.expand();
  }

  public onShowPriceUpdateInfoClick(): void {
    this.offCanvasService.show(InfoPricingUpdateComponent, null);
  }

  public goToPage(url: string): void {
    this.router.navigateByUrl(`/lodder-til-salg${url}`);
  }

  public onPricingExpand(): void {
    this.pricingUpdateExpanded = true;
  }

  public ngOnDestroy(): void {
    this.onOrderDisplayDataUpdated.unsubscribe();
    this.onOrderExpiredSubscription.unsubscribe();
    this.onOrderExpirationNoticeSubscription.unsubscribe();
    this.playerSubscription.unsubscribe();
    this.priceSubscription.unsubscribe();
  }
}
